import React, { useState } from 'react';
import { renderHTML } from '@agility/nextjs';
import { styled } from '@mui/material/styles';
import { useSession } from 'next-auth/react';
import Box from '@mui/material/Box';
import { KitButton } from 'components/common/cms-kit';
import { useTheme } from '@mui/material/styles';
import Heading5 from 'components/common/subcomponents/Typography/Heading5';
import DialogWithX from './pageComponents/DialogWithX';
import LockIcon from '@mui/icons-material/Lock';

const Container = styled('div')(({ theme }) => ({
  backgroundColor: 'rgba(191, 187, 175, 0.15)',
  [theme.breakpoints.up('md')]: {
    margin: '30px 0 40px 0',
  },
  [theme.breakpoints.down('md')]: {
    padding: '20px 0 0 0',
    margin: '15px 10px 20px 10px',
  },
}));
const IntroText = styled('div')(({ theme }) => ({
  color: theme.palette.common.black,
  margin: 5,
}));
const ButtonContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'align',
})(({ theme, align }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  [theme.breakpoints.up('md')]: {
    margin: '35px 0 25px 0',
    alignItems: align || 'center',
  },
  [theme.breakpoints.down('md')]: {
    width: '105%',
    margin: '20px 0',
    alignItems: 'center',
  },
}));
const HTMLContent = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    '> *': {
      '> *': {
        padding: 0,
        margin: 0,
        width: '1200px',
        height: '600px',
      },
    },
  },
  [theme.breakpoints.down('lg')]: {
    '> *': {
      '> *': {
        padding: 0,
        margin: 0,
        width: '300px',
        height: '600px',
      },
    },
  },
}));
const ImageContainer = styled('div')(({ theme: _theme }) => ({
  width: '100%',
}));

const TextContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fields',
})(({ fields }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  position: 'relative',
  '@media screen and (min-width: 900px)': {
    padding: '0 20px',
    width: '70%',
    paddingTop: '30px',
    paddingBottom: '30px',
    paddingRight: '5px',
    paddingLeft: '5px',
  },
  '@media screen and (max-width: 899px)': {
    padding: '0 10px 0 10px',
    width: '100%',
  },
}));

const AccentImage = styled('img', {
  shouldForwardProp: (prop) => prop !== 'fields',
})(({ fields }) => ({
  zIndex: 10,
  '@media only screen and (min-width: 992px)': {
    position: 'absolute',
    bottom: 0,
    ...(fields.imagePosition === 'left' ? { left: '-5%' } : { right: '-25%' }),
    height: '450px',
    width: 'auto',
  },
  '@media only screen and (max-width: 991px)': {
    height: 'auto',
    width: '100%',
    margin: '20px auto 30px 10%',
  },
}));

const SideBySideContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fields',
})(({ fields }) => ({
  position: 'relative',
  alignItems: 'center',
  width: '100%',
  '@media screen and (min-width: 992px)': {
    justifyContent: 'space-between',
    flexDirection: fields.imagePosition === 'left' ? 'row-reverse' : 'row',
  },
  '@media screen and (max-width: 991px)': {
    width: '90%',
    height: 'auto',
  },
}));

const FormContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '750px',
  padding: '0 0 15px 0',
});

const IntroductionWithSideImage = ({ module }) => {
  const { fields } = module;

  const { data: session } = useSession();
  const theme = useTheme();

  const [
    showTranscriptRegistrationDialog,
    setShowTranscriptRegistrationDialog,
  ] = useState(false);

  return (
    <Container>
      <Box
        sx={{
          width: '100%',
          ml: 'auto',
          mr: 'auto',
          maxWidth: { sm: '540px', md: '768px', lg: '1180px', xl: '1180px' },
        }}
      >
        <SideBySideContainer fields={fields}>
          <TextContainer fields={fields}>
            <Heading5 style={{ width: '85%' }}>{fields.title}</Heading5>
            <IntroText
              className="prose max-w-full mx-auto"
              dangerouslySetInnerHTML={renderHTML(
                session
                  ? fields.introductionTextwithSession
                    ? fields.introductionTextwithSession
                    : fields.introductionText
                  : fields.introductionText,
              )}
            />
            <ButtonContainer align={fields.buttonAlignment}>
              {fields.showButton === 'true' && (
                <>
                  <KitButton
                    round
                    style={{
                      backgroundColor: theme.palette.primary.light,
                    }}
                    onClick={(_e) => setShowTranscriptRegistrationDialog(true)}
                  >
                    <LockIcon color="white" /> {fields.buttonUrl.text}
                  </KitButton>
                </>
              )}
            </ButtonContainer>
          </TextContainer>
          <ImageContainer>
            {fields.accentImage && (
              <AccentImage
                fields={fields}
                src={fields.accentImage && fields.accentImage.url}
                alt={fields.accentImage && fields.accentImage.text}
              />
            )}
          </ImageContainer>
        </SideBySideContainer>
      </Box>
      <DialogWithX
        size="md"
        content={
          <FormContainer>
            <HTMLContent
              className="m-5 p-0"
              dangerouslySetInnerHTML={renderHTML(fields.dialogContent)}
            />
          </FormContainer>
        }
        ariaLabel="Graduate Credit Resistration Form Dialog"
        showValue={showTranscriptRegistrationDialog}
        showMethod={setShowTranscriptRegistrationDialog}
        title="Official Transcript Registration Form"
        titleBarColor="#FFA500"
        titleTextColor="#fff"
        closeXColor="#000"
      />
    </Container>
  );
};
export default IntroductionWithSideImage;
